/**
 * AddressScanner — scan a QR code containing a wallet address.
 *
 * Supports:
 * - Raw Ethereum addresses (0x...)
 * - EIP-681 payment URIs (ethereum:0x...?value=...&token=...)
 * - Plain text QR codes containing an address
 */

import { useState, useEffect, useRef, type ReactNode } from 'react';
import { createQrScanner, isQrScannerSupported, type QrScannerHandle } from '../modules/qr/qrService.js';
import { isValidAddress } from '@tokenstreaming/shared';

interface Props {
  onAddress: (address: string, amount?: string, token?: string) => void;
  onCancel: () => void;
}

type ScanState = 'initializing' | 'scanning' | 'unsupported';

/**
 * Parse scanned data to extract an Ethereum address.
 * Supports: raw address, EIP-681 URI, CAIP-10, or any text containing 0x address.
 */
function parseAddressFromScan(data: string): { address: string; amount?: string; token?: string } | null {
  const trimmed = data.trim();

  // EIP-681: ethereum:0x1234...?value=1000&token=0xABC...
  if (trimmed.startsWith('ethereum:')) {
    const withoutScheme = trimmed.slice('ethereum:'.length);
    const [addressPart, queryPart] = withoutScheme.split('?');
    const addr = addressPart?.split('/')[0] ?? '';
    if (!isValidAddress(addr)) return null;

    let amount: string | undefined;
    let token: string | undefined;
    if (queryPart) {
      const params = new URLSearchParams(queryPart);
      amount = params.get('value') ?? params.get('amount') ?? undefined;
      token = params.get('token') ?? undefined;
    }
    return { address: addr, amount, token };
  }

  // CAIP-10: eip155:56/0x1234...
  if (trimmed.includes('/0x')) {
    const parts = trimmed.split('/');
    const addr = parts[parts.length - 1] ?? '';
    if (isValidAddress(addr)) return { address: addr };
  }

  // Raw 0x address
  if (isValidAddress(trimmed)) {
    return { address: trimmed };
  }

  // Try to find a 0x address anywhere in the string
  const match = /0x[0-9a-fA-F]{40}/.exec(trimmed);
  if (match) {
    return { address: match[0] };
  }

  return null;
}

export function AddressScanner({ onAddress, onCancel }: Props): ReactNode {
  const [state, setState] = useState<ScanState>('initializing');
  const [error, setError] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<QrScannerHandle | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    void isQrScannerSupported().then((supported) => {
      if (!mountedRef.current) return;
      if (supported) {
        startScanner();
      } else {
        setState('unsupported');
      }
    });

    return () => {
      mountedRef.current = false;
      scannerRef.current?.destroy();
      scannerRef.current = null;
    };
  }, []);

  const startScanner = () => {
    setState('scanning');
    setTimeout(() => {
      if (!mountedRef.current || !videoRef.current) {
        setState('unsupported');
        return;
      }

      scannerRef.current = createQrScanner(
        videoRef.current,
        (data) => {
          if (!mountedRef.current) return;
          const result = parseAddressFromScan(data);
          if (result) {
            scannerRef.current?.stop();
            onAddress(result.address, result.amount, result.token);
          } else {
            setError('QR code does not contain a valid address');
            setTimeout(() => setError(''), 2000);
          }
        },
        (err) => {
          if (!mountedRef.current) return;
          setError(`Camera error: ${err.message}`);
          setState('unsupported');
        }
      );

      void scannerRef.current.start().catch(() => {
        if (!mountedRef.current) return;
        setError('Camera access denied');
        setState('unsupported');
      });
    }, 100);
  };

  if (state === 'unsupported') {
    return (
      <div className="screen">
        <div className="screen-body centered">
          <p className="text-secondary">Camera not available</p>
          <p className="text-muted text-sm">{error || 'QR scanning requires camera access.'}</p>
          <button className="btn-primary" onClick={onCancel}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="btn-ghost" onClick={onCancel}>Cancel</button>
        <h2>Scan Address</h2>
      </div>
      <div className="screen-body centered">
        {state === 'initializing' && (
          <>
            <div className="loading-spinner" />
            <p className="text-secondary">Starting camera...</p>
          </>
        )}

        {state === 'scanning' && (
          <>
            <p className="text-secondary text-center">
              Scan a QR code containing a wallet address
            </p>
            <div className="scanner-container">
              <video
                ref={videoRef}
                className="scanner-video"
                playsInline
                muted
                autoPlay
              />
            </div>
          </>
        )}

        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
}
